import {AnimatePresence, motion} from 'framer-motion';
import {XIcon} from 'lucide-react';
import * as React from 'react';
import {Button} from 'src/Button';
import FocusTrap from 'src/FocusTrap';
import {Overlay} from 'src/Layer/Overlay';
import {cn} from 'src/lib/utils';
import {useIsMobile} from 'src/useIsMobile';

// A dialog needs a name: the header, or a label when it has none.
type Props = {
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  dimBackground?: boolean;
  onRequestClose?: () => void;
  footer?: React.ReactNode;
} & ({header: React.ReactNode; label?: undefined} | {header?: undefined; label: string});

export const Modal = ({
  children,
  size = 'md',
  className,
  dimBackground = true,
  onRequestClose,
  header,
  label,
  footer,
}: Props) => {
  const isMobile = useIsMobile();
  const headerId = React.useId();

  const variants = isMobile
    ? {
        hidden: {y: '100%', opacity: 0},
        visible: {y: 0, opacity: 1},
        exit: {y: '100%', opacity: 0},
      }
    : {
        hidden: {transform: 'scale(0.96)', opacity: 0},
        visible: {transform: 'scale(1)', opacity: 1},
        exit: {transform: 'scale(0.96)', opacity: 0},
      };

  const dialogClassName = cn(
    'bg-background ring-ring/50 rounded-lg shadow-lg ring-1',
    'flex flex-col max-w-full overflow-hidden',
    // Mobile: use dvh to account for keyboard. top-8 = 2rem offset from top.
    'fixed top-8 left-0 right-0 h-[calc(100dvh-2rem)]',
    // Desktop: anchored to a fixed top rather than vertically centered, so a
    // modal whose content arrives asynchronously grows downward instead of
    // shifting every field by half the delta in both directions.
    'sm:inset-auto sm:top-[9vh] sm:left-1/2 sm:w-auto sm:-translate-x-1/2',
    'sm:h-auto sm:max-h-[85vh]',
    size === 'sm' && 'sm:w-sm',
    size === 'md' && 'sm:w-md',
    size === 'lg' && 'sm:w-lg',
    className,
  );

  return (
    <Overlay
      dimBackground={dimBackground}
      onOverlayClick={onRequestClose}
      blurBackground
    >
      <motion.div
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={variants}
        transition={{duration: 0.3, ease: 'easeOut'}}
        className={dialogClassName}
        role="dialog"
        aria-modal="true"
        aria-labelledby={header ? headerId : undefined}
        aria-label={label}
      >
        <FocusTrap behavior="trap">
          {header && (
            <div className="border-border flex shrink-0 flex-row items-center justify-between border-b px-6 py-4">
              <div id={headerId} className="text-lg">
                {header}
              </div>
              <Button
                onClick={onRequestClose}
                aria-label="Close"
                className="size-8"
                variant="ghost"
                size="icon"
              >
                <XIcon className="size-4" />
              </Button>
            </div>
          )}
          <div
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            className="min-h-0 flex-1 overflow-y-auto px-6 py-4"
          >
            {children}
          </div>
          {/* A footer that comes and goes while the modal is open (the create
              sheet's, on its second screens) folds away rather than jumping. */}
          <AnimatePresence initial={false}>
            {footer && (
              <motion.div
                key="footer"
                initial={{height: 0}}
                animate={{height: 'auto'}}
                exit={{height: 0}}
                transition={{duration: 0.26, ease: [0.2, 0.8, 0.2, 1]}}
                className="shrink-0 overflow-hidden"
              >
                <div className="border-border flex flex-row items-center justify-end gap-2 border-t px-6 py-4">
                  {footer}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </FocusTrap>
      </motion.div>
    </Overlay>
  );
};
